import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { HEADER_ID, PageLayout } from '@/components/templates/PageLayout';
import {
  CmBaseTable,
  CmBox,
  CmCard,
  CmLabelAndSelect,
  CmLink,
  CmLongLoading,
  CmMessageCard,
  CmNextLink,
  CmScrollBox,
  CmStack,
  CmTableBodyRow,
  CmTd,
  CmTooltip,
} from '@/components/uiParts';
import { useTargetsDomHeight } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { ommisionText } from '@/lib/util';
import {
  determineSelectedSiteId,
  TempHoursTableThs,
} from '@/pages/camel-order/temp-hour/service';
import { routes } from '@/router';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import {
  SiteListResponse,
  useSiteList,
} from '@/services/Site/hooks/list/useSiteList';
import { useTempHourList } from '@/services/TempHour/hooks/useTempHoursAPI';
import {
  formatTempHour,
  SITE_ID_QUERY_PARAM,
  TempHour,
} from '@/services/TempHour/TempHourService';

export const CONTROL_AREA_ID = 'control-area';

const TempHoursListPage: React.FC = (): JSX.Element => {
  const history = useHistory();
  const { managerAccountMe } = useManagerAccountMe();
  /* アクセス制御 */
  redirect404Page('camel_order_site_setting', history, managerAccountMe);

  const [isLoading, setIsLoading] = useState(false);
  const [sites, setSites] = useState<SiteListResponse[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [tempHours, setTempHours] = useState<TempHour[]>([]);

  const { targetsHeight } = useTargetsDomHeight([HEADER_ID, CONTROL_AREA_ID]);

  const siteUrlOptions = sites.map((site) => ({
    id: site.id,
    url: site.url,
  }));

  const loadTempHours = async (siteId: string) => {
    const { temp_hours } = await useTempHourList(siteId);
    temp_hours.sort((a, b) => {
      return a.start_time < b.start_time ? -1 : 1;
    });
    setTempHours(temp_hours);
  };

  const handleChangeSelectedSiteId = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setIsLoading(true);
    setSelectedSiteId(e.currentTarget.value);

    const url = new URL(window.location.pathname, window.location.origin);
    if (e.currentTarget.value) {
      url.searchParams.append(SITE_ID_QUERY_PARAM, e.currentTarget.value);
    }
    history.replace(url.pathname + url.search);
    await loadTempHours(e.currentTarget.value);

    setIsLoading(false);
  };

  const tempHourNewPageURL = new URL(
    routes.tempHourNew.path,
    window.location.origin
  );
  if (selectedSiteId) {
    tempHourNewPageURL.searchParams.append(SITE_ID_QUERY_PARAM, selectedSiteId);
  }

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      const sites = await useSiteList();
      setSites(sites);
      if (sites.length === 0) {
        setIsLoading(false);
        return;
      }

      const selectedSiteId = determineSelectedSiteId(sites, history);
      setSelectedSiteId(selectedSiteId);

      await loadTempHours(selectedSiteId);
      setIsLoading(false);
    })();
  }, []);

  return (
    <PageLayout title={routes.tempHourList.title}>
      <CmBox>
        <CmStack
          direction="row"
          mt="25px"
          id={CONTROL_AREA_ID}
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <CmLabelAndSelect
            label="サイトURLを選択"
            width="300px"
            fontWeight="bold"
            value={selectedSiteId}
            onChange={handleChangeSelectedSiteId}
          >
            {siteUrlOptions.map((siteUrlOption) => (
              <option key={siteUrlOption.id} value={siteUrlOption.id}>
                {siteUrlOption.url}
              </option>
            ))}
          </CmLabelAndSelect>
          <CmNextLink
            to={tempHourNewPageURL.pathname + tempHourNewPageURL.search}
          >
            新規追加
          </CmNextLink>
        </CmStack>

        {tempHours.length > 0 ? (
          <CmCard
            mt="25px"
            padding="20px"
            height={`calc(100vh - ${targetsHeight}px - 70px)`}
          >
            <CmScrollBox>
              <CmBaseTable ths={TempHoursTableThs}>
                {tempHours.map((tempHour, index) => {
                  const { delivery_stores } = tempHour;
                  const tempHourText = formatTempHour(tempHour);
                  const deliveryStoresText = delivery_stores
                    .map((deliveryStore) => deliveryStore.name)
                    .join('、');
                  const editPageUrl = new URL(
                    `/camel-order/temp-hours/${tempHour.id}`,
                    window.location.origin
                  );
                  if (selectedSiteId) {
                    editPageUrl.searchParams.append(
                      SITE_ID_QUERY_PARAM,
                      selectedSiteId
                    );
                  }
                  return (
                    <CmTableBodyRow key={index + 1}>
                      <CmTd>
                        <CmLink
                          color="blue.500"
                          to={editPageUrl.pathname + editPageUrl.search}
                        >
                          {tempHourText}
                        </CmLink>
                      </CmTd>
                      <CmTd>
                        <CmTooltip label={deliveryStoresText}>
                          {ommisionText(deliveryStoresText, 100)}
                        </CmTooltip>
                      </CmTd>
                    </CmTableBodyRow>
                  );
                })}
              </CmBaseTable>
            </CmScrollBox>
          </CmCard>
        ) : (
          <>
            {!isLoading && (
              <CmMessageCard margin="100px auto 0">
                休業日・臨時営業時間が登録されていません。
              </CmMessageCard>
            )}
          </>
        )}
        {isLoading && <CmLongLoading />}
      </CmBox>
    </PageLayout>
  );
};

export default TempHoursListPage;
